/**
 * Filter controls that live INSIDE a `DataTable` column header.
 *
 * `DataTableColumn.header` is typed `ReactNode` (widened from `string`
 * for exactly this) so a column can carry its own filter rather than
 * pushing every filter into a bar above the table, where the mapping
 * from control to column has to be inferred from a placeholder string.
 *
 * Two things every control here must do, both learned from real bugs:
 *  - `stopPropagation` on click, so interacting with the control can
 *    never bubble into a row click or a sort toggle.
 *  - Stay OUT of a sortable column's `<button>`. A `<select>` or
 *    `<input>` nested inside a button is invalid markup and behaves
 *    erratically (the button swallows the interaction). Columns that
 *    carry a filter therefore do not also offer sort.
 */
import type { ReactNode } from 'react';

const CONTROL_STYLE = {
  fontSize: 11,
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 0,
  padding: '2px 4px',
  maxWidth: '100%',
} as const;

function HeaderShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
      onClick={(e) => e.stopPropagation()}
    >
      <span>{label}</span>
      {children}
    </div>
  );
}

export interface HeaderFilterOption {
  value: string;
  label: string;
}

export function HeaderFilterSelect({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: HeaderFilterOption[];
  /** Shown for the empty/"no filter" choice, e.g. "All sites". */
  allLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <HeaderShell label={label}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`Filter by ${label.toLowerCase()}`}
        style={CONTROL_STYLE}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </HeaderShell>
  );
}

/**
 * `<input type="date">` pair. Values are `YYYY-MM-DD` strings; the
 * caller converts to epoch ms, because only the caller knows whether
 * the bound is inclusive-start or inclusive-end (see AuditPane, which
 * pushes the "to" bound to end-of-day so picking the same date twice
 * means "that whole day" rather than an empty range).
 */
export function HeaderDateRange({
  label,
  from,
  to,
  onChange,
}: {
  label: string;
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}) {
  return (
    <HeaderShell label={label}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <input
          type="date"
          value={from}
          max={to || undefined}
          onChange={(e) => onChange({ from: e.target.value, to })}
          aria-label={`${label} from`}
          style={CONTROL_STYLE}
        />
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => onChange({ from, to: e.target.value })}
          aria-label={`${label} to`}
          style={CONTROL_STYLE}
        />
      </div>
    </HeaderShell>
  );
}
